import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import VideoDialog from "@/components/VideoDialog";
import { Play, ArrowLeft, Timer, Sparkles, Dumbbell, PlayCircle } from "lucide-react";

export default function WorkoutDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [library, setLibrary] = useState([]);
  const [videoEx, setVideoEx] = useState(null);

  useEffect(() => {
    api.get(`/workouts/${id}`).then((r) => setW(r.data));
    api.get("/exercises").then((r) => setLibrary(r.data));
  }, [id]);

  const libById = useMemo(() => {
    const m = {};
    for (const e of library) m[e.id] = e;
    return m;
  }, [library]);

  const enrichEx = (ex) => ({ ...(libById[ex.exercise_id] || {}), ...ex });

  if (!w) {
    return <div className="max-w-4xl mx-auto p-8 text-[#A0A0AB]">Caricamento...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/workouts" className="text-xs uppercase tracking-widest text-[#A0A0AB] hover:text-white flex items-center gap-1 mb-6" data-testid="back-workouts">
        <ArrowLeft className="w-3 h-3" /> Schede
      </Link>

      <div className="flex items-center gap-2 mb-2">
        {w.ai_generated && (
          <span className="text-[10px] uppercase tracking-widest text-[#E2FF31] flex items-center gap-1">
            <Sparkles className="w-3 h-3" />Generata da IA
          </span>
        )}
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">{w.focus}</span>
      </div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-4">
        {w.title}
      </h1>
      <div className="flex items-center gap-4 text-sm text-[#A0A0AB] mb-8">
        <span className="flex items-center gap-1"><Timer className="w-4 h-4" />{w.duration_min} min</span>
        <span>·</span>
        <span className="flex items-center gap-1"><Dumbbell className="w-4 h-4" />{w.exercises.length} esercizi</span>
      </div>

      <button
        onClick={() => navigate(`/session/${w.id}`)}
        data-testid="start-session-btn"
        className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-4 hover:bg-[#e02e24] transition-all flex items-center gap-2 mb-8 pulse-accent"
      >
        <Play className="w-5 h-5 fill-white" /> Inizia sessione
      </button>

      <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-3">Esercizi</div>
      <div className="border border-[#2A2A35] divide-y divide-[#2A2A35] stagger">
        {w.exercises.map((ex, i) => (
          <div key={i} className="p-4 flex items-center gap-4" data-testid={`wo-ex-${i}`}>
            <div className="font-display text-4xl font-black text-[#FF3B30] w-12 flex-shrink-0">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl font-bold uppercase leading-tight">{ex.name}</div>
              <div className="text-xs text-[#A0A0AB] mt-1">
                {ex.sets} serie × {ex.reps} rip · recupero {ex.rest_seconds}s
              </div>
            </div>
            <button
              onClick={() => setVideoEx(enrichEx(ex))}
              data-testid={`wo-video-${i}`}
              className="flex-shrink-0 border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] p-2.5 transition-all"
              aria-label={`Video demo di ${ex.name}`}
              title="Guarda video demo"
            >
              <PlayCircle className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        ))}
      </div>
      <VideoDialog exercise={videoEx} onClose={() => setVideoEx(null)} />
    </div>
  );
}
